import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useAccount from './useAccount';

const NO_UNBOOKINGS = [];

// Prefix of every project's key, for refreshing them after a cross-account unbook.
export const PROJECT_EXTERNAL_UNBOOKINGS_KEY = 'projectExternalUnbookings';

// The account's own unbookings of its suppliers' employees on a project. Fetched on their own - only
// the Unbooked tab of a project with orders needs them - so the project payload does not pay for them.
const useProjectExternalUnbookings = ({ branchId, projectId, enabled = true }) => {
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const storeKey = [PROJECT_EXTERNAL_UNBOOKINGS_KEY, accountId, Number(projectId)];

  const { data, isLoading } = useQuery(
    storeKey,
    () => api.fetchProjectExternalUnbookings({ accountId, branchId, projectId }),
    { enabled: enabled && !!accountId && !!branchId && !!projectId }
  );

  // The initiator changing its own reason pick; the supplier's row follows on the server.
  const { mutateAsync: updateExternalUnbookReason } = useOptimisticMutation(
    storeKey,
    ({ unbookingId, unbookReasonId }) =>
      api.updateCrossAccountUnbookReason({
        accountId,
        branchId,
        projectId,
        shiftUnbookingId: unbookingId,
        unbookReasonId
      }),
    ({ previousData, unbookingId, unbookReasonId }) =>
      previousData && {
        ...previousData,
        data: previousData.data.map((unbooking) =>
          unbooking.id === unbookingId ? { ...unbooking, unbookReasonId } : unbooking
        )
      }
  );

  // A rebook erases the unbooking and fills a slot, so both the list and the project are stale after it.
  const { mutateAsync: rebookExternalEmployee } = useMutation(
    ({ supplierShiftId, clientShiftId, employeeId, bypassComplianceWarnings }) =>
      api.rebookOSEmployees({
        accountId,
        branchId,
        projectId,
        supplierShiftId,
        clientShiftId,
        employeeIds: [employeeId],
        bypassComplianceWarnings
      }),
    {
      onSuccess: () =>
        Promise.all([queryClient.invalidateQueries(storeKey), queryClient.invalidateQueries(['projects'])])
    }
  );

  return {
    externalUnbookings: data?.data || NO_UNBOOKINGS,
    isLoading,
    updateExternalUnbookReason,
    rebookExternalEmployee
  };
};

export default useProjectExternalUnbookings;
